/**
 * Slack連携管理用の API hooks
 */

import { RestApiError, restClient } from "lib/rest-client";
import { useCallback, useEffect, useState } from "react";

/**
 * Slack連携状況の型
 */
export type SlackIntegration = {
  isConnected: boolean;
  teamName: string | null;
  channelId: string | null;
  channelName: string | null;
  authedUserId: string | null;
  installedAt: string | null;
};

/**
 * Slackチャンネルの型
 */
export type SlackChannel = {
  id: string;
  name: string;
  isPrivate: boolean;
};

/**
 * テスト通知レスポンスの型
 */
type SlackTestResponse = {
  success: boolean;
  message: string;
};

/**
 * Slack連携状況を取得する hook
 * @return {object} integration, loading, error, refetch
 */
export function useSlackIntegration(): {
  integration: SlackIntegration | null;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [integration, setIntegration] = useState<SlackIntegration | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response =
        await restClient.get<SlackIntegration>("/settings/slack");
      setIntegration(response);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { integration, loading, error, refetch: fetch };
}

/**
 * Slack OAuth コールバックを処理する hook
 * @return {object} processCallback, loading, error
 */
export function useSlackOAuthCallback(): {
  processCallback: (code: string) => Promise<SlackIntegration>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const processCallback = useCallback(
    async (code: string): Promise<SlackIntegration> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.post<SlackIntegration>(
          "/settings/slack/oauth/callback",
          { code },
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { processCallback, loading, error };
}

/**
 * Slack連携を解除する hook
 * @return {object} disconnect, loading, error
 */
export function useDisconnectSlack(): {
  disconnect: () => Promise<void>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const disconnect = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await restClient.delete("/settings/slack");
    } catch (e) {
      const apiError =
        e instanceof RestApiError
          ? e
          : new RestApiError(
              e instanceof Error ? e.message : "Unknown error",
              500,
            );
      setError(apiError);
      throw apiError;
    } finally {
      setLoading(false);
    }
  }, []);

  return { disconnect, loading, error };
}

/**
 * Slackチャンネル一覧を取得する hook
 * @return {object} channels, loading, fetchChannels
 */
export function useSlackChannels(): {
  channels: SlackChannel[];
  loading: boolean;
  fetchChannels: () => Promise<void>;
} {
  const [channels, setChannels] = useState<SlackChannel[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchChannels = useCallback(async () => {
    setLoading(true);
    try {
      const response = await restClient.get<{ channels: SlackChannel[] }>(
        "/settings/slack/channels",
      );
      setChannels(response.channels);
    } catch {
      setChannels([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return { channels, loading, fetchChannels };
}

/**
 * 通知先チャンネルを更新する hook
 * @return {object} updateChannel, loading
 */
export function useUpdateSlackChannel(): {
  updateChannel: (channelId: string, channelName: string) => Promise<void>;
  loading: boolean;
} {
  const [loading, setLoading] = useState(false);

  const updateChannel = useCallback(
    async (channelId: string, channelName: string): Promise<void> => {
      setLoading(true);
      try {
        await restClient.put("/settings/slack/channel", {
          channelId,
          channelName,
        });
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateChannel, loading };
}

/**
 * テスト通知を送信する hook
 * @return {object} sendTest, loading
 */
export function useTestSlackNotification(): {
  sendTest: () => Promise<SlackTestResponse>;
  loading: boolean;
} {
  const [loading, setLoading] = useState(false);

  const sendTest = useCallback(async (): Promise<SlackTestResponse> => {
    setLoading(true);
    try {
      return await restClient.post<SlackTestResponse>(
        "/settings/slack/test",
        {},
      );
    } finally {
      setLoading(false);
    }
  }, []);

  return { sendTest, loading };
}
